/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { ILinkProps, Link } from '@fluentui/react/lib/Link';
import { IOverflowSetItemProps, OverflowSet } from '@fluentui/react/lib/OverflowSet';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BrandLogo } from 'src/appShell/components/BrandLogo';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';

const messages = defineMessages({
    privacyAndCookies: {
        id: 'portal.homePageFooter.privacyAndCookies',
        defaultMessage: 'Privacy & Cookies'
    },
    manageCookies: {
        id: 'portal.homePageFooter.manageCookies',
        defaultMessage: 'Manage Cookies'
    },
    termsOfUse: {
        id: 'portal.homePageFooter.termsOfUse',
        defaultMessage: 'Terms of use'
    },
    conduct: {
        id: 'portal.homePageFooter.conduct',
        defaultMessage: 'Microsoft Code of Conduct'
    },
    trademarks: {
        id: 'portal.homePageFooter.trademarks',
        defaultMessage: 'Trademarks'
    }
});

const { styleComponent, styleDiv } = getStylist('HomePageFooter');
const footerTheme = getScopedTheme('HomePageFooter');

const FooterContainer = styleComponent<IStackProps>(Stack)('HomePageFooterContainer', theme => ({
    backgroundColor: theme.containerBackgroundColor,
    height: theme.containerHeight
}));

const FooterLink = styleComponent<ILinkProps>(Link)('FooterLink', theme => ({
    marginRight: theme.linkMarginRight,
    $nest: {
        '&:hover,&:focus': {
            textDecoration: 'underline',
            textDecorationColor: theme.textColor
        }
    }
}));
const LogoLink = styleComponent<ILinkProps>(Link)('FooterLink', theme => ({ height: theme.logoLinkHeight }));

const iconButtonClassNames = classNamesFunction<IButtonProps, IButtonStyles>()({
    menuIcon: { color: footerTheme.textColor, fontSize: footerTheme.iconFontSize }
});

const Separator = styleDiv('Separator', theme => ({
    backgroundColor: theme.textColor,
    width: theme.separatorWidth,
    height: theme.logoLinkHeight
}));

/**
 * Renders the footer of the homepage.
 */
export const HomePageFooter = observer(() => {
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { isXLargeOrGreater } = useResponsiveMode();

    const { consentStore } = rootStore;

    const items: IOverflowSetItemProps[] = [
        {
            key: 'privacy',
            name: localize(messages.privacyAndCookies),
            style: { textDecoration: 'underline' },
            href: 'https://go.microsoft.com/fwlink/?LinkId=521839'
        },
        {
            key: 'tou',
            name: localize(messages.termsOfUse),
            style: { textDecoration: 'underline' },
            href: 'https://go.microsoft.com/fwlink/?LinkId=533207'
        },
        {
            key: 'codeOfConduct',
            name: localize(messages.conduct),
            style: { textDecoration: 'underline' },
            href: 'https://go.microsoft.com/fwlink/?LinkId=698895'
        },
        {
            key: 'trademarks',
            name: localize(messages.trademarks),
            style: { textDecoration: 'underline' },
            href: 'https://www.microsoft.com/legal/intellectualproperty/trademarks'
        }
    ];

    /**
     * Renders the items to display in the overflow set.
     *
     * @param item The item to display
     * @returns An item to display in the footer.
     */
    const onRenderItem = (item: IOverflowSetItemProps) => (
        <FooterLink as="a" key={item.key} href={item.href as string} target="_blank" role="link" rel="noopener noreferrer">
            <LuisText fontWeight={500} color={footerTheme.textColor}>
                {item.name as string}
            </LuisText>
        </FooterLink>
    );

    /**
     * Renders the "more" button to display additional
     * overflow items when screen is small.
     *
     * @returns An icon button for the more option.
     */
    const onRenderMoreButton = () => (
        <IconButton styles={iconButtonClassNames} menuIconProps={{ iconName: 'More' }} menuProps={{ items }} />
    );

    return (
        <FooterContainer horizontal as="footer" horizontalAlign="center" verticalAlign="center" tokens={{ childrenGap: 15 }}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 15 }}>
                <LogoLink href="https://www.microsoft.com/" target="_blank" rel="noopener noreferrer">
                    <BrandLogo
                        defaultLogo="whiteLogo"
                        alt="Microsoft"
                        whiteLogoBlobPath="assets/images/logos/microsoftLogoWhite.svg"
                        blackLogoBlobPath="assets/images/logos/microsoftLogoBlack.svg"
                    />
                </LogoLink>

                <Separator role="presentation" />
            </Stack>

            <LuisText variant="smallPlus" color={footerTheme.textColor}>
                © {new Date().getFullYear()} Microsoft
            </LuisText>

            {consentStore.isRequired && (
                <Link onClick={() => consentStore.innerConsentObject.manageConsent()}>
                    <LuisText variant="medium" fontWeight={500} color={footerTheme.textColor}>
                        {localize(messages.manageCookies)}
                    </LuisText>
                </Link>
            )}

            <OverflowSet
                items={isXLargeOrGreater ? items : []}
                overflowItems={isXLargeOrGreater ? [] : items}
                onRenderItem={onRenderItem}
                onRenderOverflowButton={onRenderMoreButton}
            />
        </FooterContainer>
    );
});
